import cn from 'classnames';
import React, { memo, useCallback, useEffect, useState } from 'react';

import Text from 'components/atoms/text';

import sm from './styles.module.scss';

export interface Tab {
  label: string;
  value: string;
  path?: string;
}

interface Props {
  items: Tab[];
  defaultTab?: string;
  onClick?: (tab: Tab) => void;
  withCenterAlign?: boolean;
  staticTabWidth?: number;
  staticTabHeight?: number;
}

type HandleTabClick = (tab: Tab) => EmptyArgsVoidFunc;
type RenderTab = (tab: Tab) => JSX.Element;

const Tabs = ({
  items,
  defaultTab,
  onClick,
  withCenterAlign,
  staticTabWidth,
  staticTabHeight,
}: Props) => {
  const [activeTab, setActiveTab] = useState('');
  const handleTabClick: HandleTabClick = useCallback(
    (tab) => () => {
      setActiveTab(tab.value);
      if (onClick) onClick(tab);
    },
    [onClick]
  );

  /**
   * This useEffect sets the active tab based on what page we are looking at. The problem with setting activeTab at
   * line 34 is that the activeTab is not reset even though it is a different page.
   */
  useEffect(() => {
    setActiveTab(defaultTab ?? items[0].value);
  }, [defaultTab, items]);

  const renderTab: RenderTab = useCallback(
    (tab) => {
      const isActive = tab.value === activeTab;
      return (
        <button
          key={tab.value}
          type="button"
          onClick={handleTabClick(tab)}
          style={{ width: staticTabWidth, height: staticTabHeight }}
          className={cn([sm.Container_Tab], {
            [sm.Container_Tab_Active]: isActive,
          })}
        >
          <Text content={tab.label} with16Font withGreyColor={!isActive} />
          {isActive && <div className={sm.Indicator} />}
        </button>
      );
    },
    [activeTab, handleTabClick, staticTabHeight, staticTabWidth]
  );

  return (
    <div
      className={cn([sm.Container], {
        [sm.Container_WithCenterAlign]: withCenterAlign,
      })}
    >
      {items.map(renderTab)}
    </div>
  );
};

export default memo(Tabs);
